import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeModeProvider } from './contexts/ThemeContext';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import ChatWidget from './components/Chat/ChatWidget';
import ErrorBoundary from './components/ErrorBoundary';

// Global styles
import './styles/animations.css';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const QuotationTool = lazy(() => import('./pages/QuotationTool'));
const DetailedQuotation = lazy(() => import('./pages/DetailedQuotation'));
const QuotationView = lazy(() => import('./pages/QuotationView'));
const QuotationInvoice = lazy(() => import('./pages/QuotationInvoice'));
const AdminPricingPanel = lazy(() => import('./pages/AdminPricingPanel'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const Chatbot = lazy(() => import('./pages/Chatbot'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const EventCalendar = lazy(() => import('./pages/EventCalendar'));
const VirtualShowcase = lazy(() => import('./pages/VirtualShowcase'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const EventMarketingPolicy = lazy(() => import('./pages/EventMarketingPolicy'));
const StallFabrication = lazy(() => import('./pages/StallFabrication'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const EditServicePage = lazy(() => import('./pages/EditServicePage'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const Register = lazy(() => import('./pages/Register'));
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Loading component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);


const AppContent = () => {
  const location = useLocation();

  return (
    <>
      <Navbar />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/quotation" element={<QuotationTool />} />
          <Route
            path="/quotation/detailed"
            element={
              <ProtectedRoute>
                <DetailedQuotation />
              </ProtectedRoute>
            }
          />
          <Route path="/quotation/:quotationId/view" element={<QuotationView />} />
          <Route path="/quotation/:quotationId/invoice" element={<QuotationInvoice />} />
          <Route path="/quotation/:quotationId/payment" element={<PaymentPage />} />
          <Route path="/booking/:trackingId" element={<BookingPage />} />
          <Route path="/chatbot" element={<Chatbot />} />
          <Route path="/calendar" element={<EventCalendar />} />
          <Route path="/showcase" element={<VirtualShowcase />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/event-marketing-policy" element={<EventMarketingPolicy />} />
          <Route path="/stall-fabrication" element={<StallFabrication />} />
          <Route path="/service/:serviceKey" element={<ServicePage />} />
          <Route path="/setup-events" element={<ServicePage serviceKeyProp="setup_events" />} />
          <Route path="/corporate-events" element={<ServicePage serviceKeyProp="corporate_events" />} />
          <Route path="/audio-visual-rental" element={<ServicePage serviceKeyProp="audio_visual_rental" />} />
          <Route path="/mall-activity" element={<ServicePage serviceKeyProp="mall_activity" />} />
          <Route path="/stall-fabrication-new" element={<ServicePage serviceKeyProp="stall_fabrication" />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/:tab"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pricing"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPricingPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/services/edit/:serviceKey"
            element={
              <ProtectedRoute requiredRole="admin">
                <EditServicePage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {!location.pathname.startsWith('/admin') && <Footer />}
      {location.pathname !== '/chatbot' && !location.pathname.startsWith('/admin') && <ChatWidget />}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeModeProvider>
        <AuthProvider>
          <DataProvider>
            <Router>
              <AppContent />
            </Router>
          </DataProvider>
        </AuthProvider>
      </ThemeModeProvider>
    </ErrorBoundary>
  );
}

export default App;

